import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { referralApi, inviteLink } from '../api/referral'
import ContactFriendListItem from '../components/ContactFriendListItem'
import { Friend } from '../types/friend'
import { userApi } from '../api/user'
import { FollowStatus } from '../constants/follow'
import { isApp } from '../utils/platform'
import { toastUtil } from '../utils/toast'
import { dialogUtil } from '../utils/dialog'

// 화면에 한 번에 더 보여줄 개수 (클라이언트 측 점진 렌더 — 네트워크 호출 없음)
const DISPLAY_CHUNK = 50
// 서버로 한 번에 보낼 전화번호 수 (서버 부하 방지)
const PHONE_CHECK_BATCH = 300
// 다음 청크가 도착하지 않을 때 수집을 끝내는 안전 타임아웃(ms)
const COLLECT_TIMEOUT = 7000

// 전화번호 정규화: 숫자만 남기고 +82/0082 국가코드를 010… 형태로 변환
const normalizePhone = (raw: string): string => {
  let p = (raw || '').replace(/[^0-9]/g, '')
  if (p.startsWith('0082')) p = '0' + p.slice(4)
  else if (p.startsWith('82')) p = '0' + p.slice(2)
  return p
}

const ContactFriends: React.FC = () => {
  const navigate = useNavigate()

  /*
   * 어디서 들어왔나.
   *   (없음)  가입 온보딩 — 끝나면 온보딩 순위 화면으로.
   *   invite  친구 초대 — 계정 없는 사람만 보여 주고(모두 '메시지'), 문자에 내 추천인 코드를 넣는다.
   *   follow  팔로워 목록 '친구 찾기'.
   * invite·follow 는 끝나면 들어온 화면으로 돌아간다. 예전에는 어디서 왔든 온보딩
   * 순위 화면으로 빠졌다.
   */
  const [searchParams] = useSearchParams()
  const from = searchParams.get('from')
  const inviteMode = from === 'invite'
  const exit = useCallback(() => {
    if (from) navigate(-1)
    else navigate('/onboarding-rank')
  }, [from, navigate])

  // 초대 문자에 넣을 내 추천인 코드(초대 모드에서만 받는다)
  const myCodeRef = useRef<string | null>(null)
  useEffect(() => {
    if (!inviteMode) return
    referralApi.getMine().then((r) => { myCodeRef.current = r.code }).catch(() => {})
  }, [inviteMode])

  // 최종 정렬된 전체 친구 목록 (앱 사용자 상단 + 가나다순)
  const [friends, setFriends] = useState<Friend[]>([])
  // 수집 + 사용자 확인 진행 중 여부
  const [loading, setLoading] = useState(true)
  // 화면에 렌더할 개수 (점진 렌더)
  const [displayCount, setDisplayCount] = useState(DISPLAY_CHUNK)

  const currentUserPhoneRef = useRef<string | null>(null)
  const rawContactsRef = useRef<any[]>([]) // 누적된 원본 연락처
  const totalRef = useRef<number>(0)
  const startedRef = useRef(false)
  const finalizedRef = useRef(false)
  const collectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null)

  // 앱 환경 체크
  useEffect(() => {
    if (!isApp()) {
      toastUtil.error('앱으로 실행해주세요')
      navigate(-1)
    }
  }, [navigate])

  // 현재 사용자 전화번호 (본인 제외용)
  useEffect(() => {
    userApi
      .getProfile()
      .then((res) => {
        currentUserPhoneRef.current = res.user.phone ?? null
      })
      .catch(() => {})
  }, [])

  // 원본 연락처 → Friend[] (전화번호 있는 것만, 본인 제외, 정규화 기준 중복 제거)
  const buildFriendList = useCallback((): Friend[] => {
    const selfPhone = currentUserPhoneRef.current
      ? normalizePhone(currentUserPhoneRef.current)
      : null
    const seen = new Set<string>()
    const out: Friend[] = []
    rawContactsRef.current.forEach((c, i) => {
      if (!c?.phones || !Array.isArray(c.phones) || c.phones.length === 0) return
      const phone = c.phones[0]
      const norm = normalizePhone(phone)
      if (!norm) return
      if (selfPhone && norm === selfPhone) return
      if (seen.has(norm)) return
      seen.add(norm)
      const name = c.displayName || c.givenName || '이름 없음'
      out.push({
        id: `contact-${i}-${norm}`,
        name,
        initial: name.charAt(0).toUpperCase() || '?',
        phone,
        isFollowing: false,
      })
    })
    return out
  }, [])

  // 전체 수집 완료 → 전화번호 일괄 확인 → 정렬 → 표시
  const finalize = useCallback(async () => {
    if (finalizedRef.current) return
    finalizedRef.current = true
    if (collectTimerRef.current) clearTimeout(collectTimerRef.current)

    const list = buildFriendList()
    if (list.length === 0) {
      setFriends([])
      setLoading(false)
      return
    }

    // 전화번호를 배치로 나눠 일괄 확인 (서버 부하 방지)
    const resultMap: Record<
      string,
      { user: { id: string; profile_image: string | null } | null; follow_status: FollowStatus }
    > = {}
    const phones = list.map((f) => normalizePhone(f.phone))
    for (let i = 0; i < phones.length; i += PHONE_CHECK_BATCH) {
      const batch = phones.slice(i, i + PHONE_CHECK_BATCH)
      try {
        const res = await userApi.findUsersByPhone({ phones: batch })
        Object.assign(resultMap, res.results)
      } catch (error) {
        console.error('전화번호 확인 실패(배치):', error)
      }
    }

    const enriched: Friend[] = list.map((f) => {
      const r = resultMap[normalizePhone(f.phone)]
      if (r && r.user) {
        return {
          ...f,
          userId: r.user.id,
          profileImage: r.user.profile_image ?? null,
          followStatus: r.follow_status as FollowStatus,
        }
      }
      return f
    })

    // 정렬: 앱 사용자(userId 있음) 상단 → 가나다, 그 다음 미사용자 → 가나다
    enriched.sort((a, b) => {
      const au = a.userId ? 0 : 1
      const bu = b.userId ? 0 : 1
      if (au !== bu) return au - bu
      return a.name.localeCompare(b.name, 'ko')
    })

    // 친구 초대: 이미 가입한 사람은 초대 대상이 아니다 — 계정 없는 사람만 남긴다
    const shown = inviteMode ? enriched.filter((f) => !f.userId) : enriched
    const userCount = shown.filter((f) => f.userId).length
    setFriends(shown)
    // 앱 사용자는 처음부터 전부 보이도록 초기 렌더 개수 확보
    setDisplayCount(Math.max(DISPLAY_CHUNK, userCount))
    setLoading(false)
  }, [buildFriendList, inviteMode])

  // 다음 청크 요청 (없으면 수집 종료)
  const requestNext = useCallback(() => {
    const received = rawContactsRef.current.length
    const bridge = (window as any).FlutterBridge
    if (bridge?.requestMoreContacts && received < totalRef.current) {
      if (collectTimerRef.current) clearTimeout(collectTimerRef.current)
      // 다음 청크가 안 오면 가진 것만으로 마무리
      collectTimerRef.current = setTimeout(() => finalize(), COLLECT_TIMEOUT)
      bridge.requestMoreContacts(received)
    } else {
      finalize()
    }
  }, [finalize])

  // 연락처 수집 시작 (첫 청크는 localStorage, 이후는 onContactsData)
  useEffect(() => {
    if (startedRef.current) return
    if (!isApp()) return
    startedRef.current = true

    const bridge = (window as any).FlutterBridge
    if (bridge) {
      bridge.onContactsData = (data: any) => {
        if (data && Array.isArray(data.contacts)) {
          if (data.total != null) totalRef.current = data.total
          rawContactsRef.current = [
            ...rawContactsRef.current,
            ...data.contacts,
          ]
        }
        requestNext()
      }
    }

    const str = localStorage.getItem('contacts_data')
    if (!str) {
      exit()
      return
    }
    try {
      const parsed = JSON.parse(str)
      const contacts = parsed.contacts ?? parsed
      const total =
        parsed.total ?? (Array.isArray(contacts) ? contacts.length : 0)
      localStorage.removeItem('contacts_data')

      if (!Array.isArray(contacts) || contacts.length === 0) {
        exit()
        return
      }
      rawContactsRef.current = [...contacts]
      totalRef.current = total
      requestNext()
    } catch {
      exit()
    }

    return () => {
      if (bridge?.onContactsData) delete bridge.onContactsData
      if (collectTimerRef.current) clearTimeout(collectTimerRef.current)
    }
  }, [navigate, requestNext, exit])

  // 점진 렌더: 더 보여줄 게 있으면 sentinel 진입 시 표시 개수 증가 (네트워크 없음)
  const hasMoreToShow = displayCount < friends.length
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current
    if (!sentinel || !hasMoreToShow) return
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setDisplayCount((c) => Math.min(c + DISPLAY_CHUNK, friends.length))
        }
      },
      { rootMargin: '200px 0px' }
    )
    io.observe(sentinel)
    return () => io.disconnect()
  }, [hasMoreToShow, friends.length])

  const handleSendAppLink = async (friend: Friend) => {
    if (!(window as any).FlutterBridge) {
      toastUtil.error('SMS 앱을 열 수 없습니다')
      return
    }

    const frontendUrl = (window as any).FlutterBridge.frontendUrl
    if (!frontendUrl) {
      toastUtil.error('프론트엔드 URL을 가져올 수 없습니다')
      return
    }

    try {
      // 미사용 유저 초대는 웹이 아니라 '앱 다운로드' 랜딩으로 (기기별 스토어 자동 이동 + 링크 미리보기 카드)
      const code = inviteMode ? myCodeRef.current : null
      // 초대 모드면 링크에도 내 코드를 유입 표시로 붙인다(inviteLink)
      const downloadUrl = inviteLink(`${frontendUrl.replace(/\/$/, '')}/download.html`, code)
      const message = code
        ? `${friend.name}님, TASTEE에서 취향을 함께 기록해요! 가입할 때 추천인 코드 ${code} 를 입력해 주세요 ${downloadUrl}`
        : `${friend.name}님, TASTEE에서 취향을 함께 기록해요! ${downloadUrl}`
      ;(window as any).FlutterBridge.openSMS({
        phone: friend.phone,
        message: message,
      })
    } catch (error) {
      console.error('SMS 앱 열기 실패:', error)
      toastUtil.error('SMS 앱을 열 수 없습니다')
    }
  }

  const handleFollowClick = async (friend: Friend) => {
    if (!friend.userId) return

    // 서버 응답을 기다리지 않고 버튼을 먼저 바꾼다(팔로우 API가 수백 ms 걸림).
    // 실패하면 눌리기 전 상태로 되돌린다.
    const previousStatus = friend.followStatus
    const applyStatus = (next: FollowStatus | undefined) => {
      setFriends((prevFriends) =>
        prevFriends.map((f) =>
          f.id === friend.id ? { ...f, followStatus: next } : f
        )
      )
    }

    try {
      if (friend.followStatus === FollowStatus.NONE) {
        applyStatus(FollowStatus.REQUESTED)
        await userApi.requestFollow(friend.userId)
        toastUtil.success('팔로우 신청이 완료되었습니다')
      } else if (
        friend.followStatus === FollowStatus.REQUESTED ||
        friend.followStatus === FollowStatus.FOLLOWING
      ) {
        applyStatus(FollowStatus.NONE)
        await userApi.cancelFollow(friend.userId)
        toastUtil.success('팔로잉이 해제되었습니다')
      }
    } catch (error) {
      applyStatus(previousStatus)
      console.error('팔로우 처리 실패:', error)
    }
  }

  const handleSkip = async () => {
    // 초대·팔로워 목록에서 들어왔으면 묻지 않고 돌아간다(연동 여부와 상관없는 자리)
    if (from) {
      exit()
      return
    }
    const confirmed = await dialogUtil.confirm({
      message: '연락처 연동을 하지 않으면\n친구 추천 기능이 제한됩니다',
      confirmText: '건너뛰기',
      cancelText: '취소',
    })
    if (confirmed) {
      exit()
    }
  }

  const visibleFriends = friends.slice(0, displayCount)

  return (
    <div className="w-full h-full flex flex-col">
      <div className="w-full h-full flex flex-col px-16px py-40px">
        <div className="app-container overflow-y-auto scrollbar-hide flex-1 flex flex-col gap-16px mb-24px">
          {loading ? (
            <div className="text-center text-textColorText2 py-40px">
              연락처 확인 중...
            </div>
          ) : friends.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-center text-textColorText2 empty-state-center">
              {inviteMode ? '초대할 친구가 없어요' : '연락처가 없습니다'}
            </div>
          ) : (
            <>
              {visibleFriends.map((friend) => (
                <ContactFriendListItem
                  key={friend.id}
                  friend={friend}
                  onFollowClick={handleFollowClick}
                  onSendAppLinkClick={handleSendAppLink}
                />
              ))}
              {hasMoreToShow && (
                <div
                  ref={loadMoreSentinelRef}
                  className="w-full py-16px text-center text-textColorText2 text-14px"
                />
              )}
            </>
          )}
        </div>

        <button
          type="button"
          onClick={handleSkip}
          className="w-full p-15px font-bold text-16px text-brown-80%-main"
        >
          {/* 친구 초대에서 와도 온보딩처럼 '건너뛰기'(2026-11-04). 팔로워 목록에서 오면 '완료' */}
          {from && !inviteMode ? '완료' : '건너뛰기'}
        </button>
      </div>
    </div>
  )
}

export default ContactFriends
